/**
 * The buttons at the end of a table row, rendered the same way every time.
 *
 * Before this existed, four screens had four conventions: a text button for
 * "Edit" next to an icon for "Delete" on channels, two icons on rules, a text
 * button for "Retry" on deliveries, one for "Restore" on trash. Nothing was
 * wrong with any of them individually, which is exactly why they drifted.
 *
 * So the *shape* is not a per-page choice. An action names itself and supplies
 * an icon; this decides how it looks. A page that wants a text button wants a
 * page-level action in `PageHeader`, not a row action.
 *
 * ## The two things it gets right centrally
 *
 * **A tooltip on a disabled button never appears.** MUI's `Tooltip` listens on
 * its child, and a disabled button raises no pointer events — so the one action
 * whose label the user most needs ("why can I not press this?") is the one with
 * no label. The fix is a `<span>` between them, and it is the kind of thing that
 * gets remembered on three screens out of four.
 *
 * **An icon button has no accessible name.** `label` becomes both the tooltip
 * and `aria-label`, so an icon-only row is not a row of unnamed buttons to a
 * screen reader. That is why `label` is required rather than optional.
 */
import type { ReactNode } from 'react';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

export interface EntityAction {
  /** React key. Also what a page identifies the action by in a test. */
  key: string;
  /** The tooltip *and* the accessible name. Always a real verb phrase. */
  label: string;
  icon: ReactNode;
  onClick: () => void;
  /** `error` for anything that destroys something. Nothing else is coloured. */
  tone?: 'default' | 'error';
  /**
   * Visible but unpressable — a request is in flight, or this row cannot take
   * it right now. The tooltip still works; that is the point of the span.
   */
  disabled?: boolean;
  /**
   * Not rendered at all. For "the caller lacks the permission" — an action
   * nobody may ever press should not be on screen greyed out, teasing.
   */
  hidden?: boolean;
}

export interface RowActionsProps {
  actions: readonly EntityAction[];
}

export function RowActions({ actions }: RowActionsProps) {
  const visible = actions.filter((action) => !action.hidden);
  if (visible.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
      {visible.map((action) => (
        <Tooltip key={action.key} title={action.label}>
          {/* The span is load-bearing: see the note at the top. */}
          <span>
            <IconButton
              size="small"
              aria-label={action.label}
              disabled={action.disabled ?? false}
              color={action.tone === 'error' ? 'error' : 'default'}
              onClick={action.onClick}
            >
              {action.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Stack>
  );
}
